import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useInView, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from 'motion/react';
import { ArrowRight, Menu, X, ChevronRight } from 'lucide-react';

// ── Assets ───────────────────────────────────────────────────────────────────
const ASSETS = {
  logo: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-f32cfc5b-fd02-4bf6-a696-340ab4e74611-logo.png',
  hero2: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-f01268eb-ef10-4439-9a47-45850a7c33d7-hero2.jpg',
  food1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-5f275207-086e-4b62-8d86-237267148e57-food1.jpg',
  robotTop: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-81f567e4-1313-47f4-ab3b-b39ab80c8d43-robot-top.png',
  robot: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-4df1828f-9de4-4b6c-96ff-0b50533440c6-robot.jpg',
  materials1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-e15c1aa8-3df4-4552-b9ee-c34df5627b74-materials1.jpg',
  pharma1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-e4a4e4a8-98c0-4e16-91d5-93f9e86cbb52-pharma1.jpg',
  industrial1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-8ad104c8-105d-41f4-bb0d-7ca7027e8a46-industrial1.jpg',
  retail1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-5bda7fe6-b41b-4761-a332-0099b51e4ae3-retail1.png',
  retail2: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-66e6eaa2-f0c8-40f3-9f15-6c10aab1ec68-retail2.jpg',
  sectorsBg: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-5a8ecd5e-483e-431b-a9b8-f2c99d8c854e-sectors-bg.png',
  hero1: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-e466df2b-d316-4c08-8d17-6ebbed6f9875-hero1.png',
  hero3: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-8ed9d66b-1019-4768-bf1f-68eddc9fe224-hero2.png',
  hero4: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-7cd929fb-c629-4eb5-8097-16f47885af28-hero3.png',
  heroPrint: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-91f2ab21-b12e-47c5-9989-ebe3e0d1b47a-hero4.png',
  extra2: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-014c72cd-d1bf-46c5-8d4a-edf0de1eaec1-extra2.png',
  extra3: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-54bf24c8-0de3-48be-80e3-6c3d0c4aed63-extra3.png',
  extra4: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-f692dfeb-4a17-474c-a0c0-8cf5768a25cf-extra4.png',
  extra8: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-e6dea194-592a-4b83-ab67-f537ef5e9e24-extra8.png',
  extra9: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/flexivo-0ce40799-8f18-4f75-bb95-0e8390add25a-extra9.png',
};

// ── Colors ───────────────────────────────────────────────────────────────────
const C = {
  cream: 'rgb(255,254,251)',
  olive: 'rgb(91,92,48)',
  oliveLight: 'rgb(153,159,86)',
  sand: 'rgb(252,246,232)',
  dark: 'rgb(17,17,17)',
};

// ── Carousels ────────────────────────────────────────────────────────────────
const ROBOT_SLIDES = [
  ASSETS.robotTop,
  ASSETS.extra2,
  ASSETS.extra3,
  ASSETS.extra4,
];

const RETAIL1_SLIDES = [
  ASSETS.retail1,
  ASSETS.extra8,
  ASSETS.extra9,
];

// ── Global CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  html { scroll-behavior: smooth; }
  * { box-sizing: border-box; }

  @media (max-width: 767px) {
    .flexivo-hero-grid  { grid-template-columns: 1fr !important; height: auto !important; }
    .flexivo-hero-grid > div { min-height: 300px; }
    .flexivo-sectors-grid { grid-template-columns: 1fr !important; height: auto !important; }
    .flexivo-sectors-left  { padding: 40px 24px !important; }
    .flexivo-sectors-right { padding: 40px 24px !important; }
    .flexivo-sectors-divider { display: none !important; }
    .flexivo-2col { grid-template-columns: 1fr !important; }
    .flexivo-footer-strip { grid-template-columns: repeat(3, 1fr) !important; height: 180px !important; }
    .flexivo-footer-links { font-size: 16px !important; }
    .flexivo-product-pill { font-size: 16px !important; padding: 0 20px !important; height: 40px !important; }
  }

  .nav-link {
    position: relative;
    color: rgb(17,17,17);
    font-size: 18px;
    font-weight: 400;
    text-decoration: none;
    cursor: pointer;
    padding-bottom: 2px;
  }
  .nav-link::after {
    content: '';
    position: absolute;
    left: 0; bottom: 0;
    width: 0; height: 1.5px;
    background: rgb(91,92,48);
    transition: width 0.28s ease;
  }
  .nav-link:hover::after { width: 100%; }
  .nav-link:hover { opacity: 1 !important; }

  .card-bg {
    transition: transform 0.65s cubic-bezier(0.25, 0.1, 0.25, 1);
    will-change: transform;
  }
  .card-wrap:hover .card-bg { transform: scale(1.045); }

  .pill-btn {
    display: inline-flex;
    align-items: center;
    height: 50px;
    padding: 0 35px;
    border-radius: 200px;
    border: 1px solid #fff;
    color: #fff;
    font-size: clamp(14px, 1.8vw, 23.86px);
    white-space: nowrap;
    background: transparent;
    text-decoration: none;
    transition: background 0.22s ease, color 0.22s ease, transform 0.22s ease;
  }
  .pill-btn:hover {
    background: #fff;
    color: rgb(91,92,48);
    border-color: rgb(91,92,48);
    transform: translateY(-2px);
  }

  .sector-link {
    display: block;
    padding: 6px 0;
    color: rgba(255,255,255,0.72);
    cursor: default;
    transition: padding-left 0.22s ease, color 0.22s ease;
  }
  .sector-link:hover { padding-left: 12px; color: #fff; }

  .footer-img-item {
    transition: transform 0.5s ease, filter 0.5s ease;
    filter: brightness(0.85);
  }
  .footer-img-item:hover { transform: scale(1.04); filter: brightness(1); }

  .cta-arrow {
    transition: transform 0.25s ease;
  }
  .cta-link:hover .cta-arrow { transform: translateX(5px); }
`;

// ── Shared canvas setup util ─────────────────────────────────────────────────
function setupCanvas(canvas: HTMLCanvasElement) {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.parentElement!.getBoundingClientRect();
  canvas.width  = rect.width  * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  return { ctx, W: rect.width, H: rect.height, dpr };
}
function hexRgb(h: string): [number,number,number] {
  return [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
}
type MousePos = { x: number; y: number };

// ── CTACard: cream card with single radial ripple that covers both zones on hover
type Ripple = { id: number; x: number; y: number; r: number };

function CTACard() {
  const upperRef = useRef<HTMLAnchorElement>(null);
  const [hovered, setHovered] = useState(false);
  const [ripple,  setRipple]  = useState<Ripple | null>(null);
  const idRef = useRef(0);

  const handleEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!upperRef.current) return;
    const r = upperRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    // Radius needed to reach the furthest corner from the cursor → covers entire upper area
    const maxR = Math.hypot(Math.max(x, r.width - x), Math.max(y, r.height - y)) + 20;
    setHovered(true);
    setRipple({ id: idRef.current++, x, y, r: maxR });
  };
  const handleLeave = () => {
    setHovered(false);
    setRipple(null);
  };

  const CREAM      = 'rgb(252,246,232)';
  const OLIVE      = 'rgb(153,159,86)';
  const DARK_OLIVE = 'rgb(91,92,48)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
      className="flex flex-col overflow-hidden"
      style={{ position: 'relative', borderRadius: 14, background: CREAM }}
    >
      {/* Upper area (cream) — this is the WhatsApp link. The region bar below
          is deliberately left outside it, so only the cream zone is clickable. */}
      <a
        ref={upperRef}
        href="https://wa.me/573145551717"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contactanos hoy por WhatsApp"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className="flex-1 relative flex items-center justify-center no-underline"
        style={{ overflow: 'hidden', textDecoration: 'none', cursor: 'pointer' }}
      >
        {/* Single radial ripple from cursor that grows to cover the whole upper area */}
        <AnimatePresence>
          {ripple && (
            <motion.div
              key={ripple.id}
              aria-hidden
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'absolute',
                left: ripple.x - ripple.r,
                top:  ripple.y - ripple.r,
                width:  ripple.r * 2,
                height: ripple.r * 2,
                borderRadius: '50%',
                background: OLIVE,
                pointerEvents: 'none',
                transformOrigin: 'center',
                zIndex: 1,
              }}
            />
          )}
        </AnimatePresence>

        {/* CTA content */}
        <div className="inline-flex items-center gap-[10px] relative" style={{ zIndex: 10 }}>
          <span style={{
            fontWeight: 700,
            fontSize: 'clamp(24px, 2.8vw, 41px)',
            letterSpacing: '-1.656px',
            lineHeight: 1,
            color: hovered ? CREAM : DARK_OLIVE,
            transition: 'color 0.35s ease',
          }}>
            Contáctanos hoy
          </span>
          <span
            className="cta-arrow inline-flex items-center justify-center"
            style={{
              width: 40, height: 40, borderRadius: '50%',
              background: hovered ? CREAM : DARK_OLIVE,
              transition: 'background 0.35s ease',
              flexShrink: 0,
            }}
          >
            <ArrowRight size={16} color={hovered ? DARK_OLIVE : '#fff'} />
          </span>
        </div>
      </a>

      {/* Colombia bar — outside the link on purpose */}
      <div className="relative flex items-center overflow-hidden"
           style={{ height: 100, padding: '0 22px', flexShrink: 0, background: OLIVE }}>
        <span style={{ fontWeight: 700, fontSize: 27, color: CREAM }}>
          Colombia
        </span>
      </div>
    </motion.div>
  );
}

// ── Utility: smooth scroll to section ────────────────────────────────────────
function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// ── FadeUp: reusable scroll-triggered entrance ────────────────────────────────
interface FadeUpProps {
  children: React.ReactNode;
  delay?: number;
  direction?: 'up' | 'left' | 'right';
  className?: string;
  style?: React.CSSProperties;
}
function FadeUp({ children, delay = 0, direction = 'up', className, style }: FadeUpProps) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const initial = direction === 'left' ? { opacity: 0, x: -48 }
                : direction === 'right' ? { opacity: 0, x: 48 }
                : { opacity: 0, y: 36 };
  return (
    <motion.div
      ref={ref}
      initial={initial}
      animate={inView ? { opacity: 1, x: 0, y: 0 } : {}}
      transition={{ duration: 0.72, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}

// ── Slideshow ────────────────────────────────────────────────────────────────
function Slideshow({ images }: { images: string[] }) {
  const [index, setIndex] = useState(0);
  const [previous, setPrevious] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => {
        setPrevious(i);
        return (i + 1) % images.length;
      });
    }, 4500);
    return () => clearInterval(id);
  }, [images.length]);

  // Every slide is mounted from the start, at opacity 0 — that is what makes
  // the browser fetch and decode them up front. Mounting only the visible slide
  // makes each image download the first time it is due, and a half-decoded
  // background paints as a black card.
  //
  // The outgoing slide is also held at opacity 1 underneath the incoming one:
  // fading both at once lets the black tile behind them show through the middle
  // of every transition.
  return (
    <>
      {images.map((src, i) => (
        <motion.div
          key={src}
          className="absolute inset-0 card-bg"
          initial={false}
          animate={{ opacity: i === index || i === previous ? 1 : 0 }}
          transition={{ duration: i === index ? 0.9 : 0, ease: [0.25, 0.1, 0.25, 1] }}
          style={{
            backgroundImage: `url('${src}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: i === index ? 2 : i === previous ? 1 : 0,
          }}
        />
      ))}
    </>
  );
}

// ── CatalogCard: the right-side card of the Sectors+Products section ─────────
function CatalogCard({ products }: { products: string[] }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const inView  = useInView(cardRef, { once: true, amount: 0.15 });

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, x: 48 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.72, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
      className="flexivo-sectors-right relative h-full"
      style={{ padding: '65px 81px' }}
    >
      <div style={{ fontWeight: 500, fontSize: 20, letterSpacing: '2.863px', lineHeight: '30.673px', color: 'rgba(252,246,232,0.5)' }}>
        CATÁLOGO
      </div>
      <div id="productos" style={{ fontSize: 'clamp(28px, 3.5vw, 47.7px)', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.852px', color: '#fff', whiteSpace: 'pre-line', marginTop: 16 }}>
        {`Productos que\nofrecemos`}
      </div>
      <div className="flex flex-wrap gap-3" style={{ maxWidth: 800, marginTop: 32 }}>
        {products.map((product, i) => (
          <motion.div
            key={product}
            className="pill-btn"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35, delay: 0.15 + i * 0.045 }}
          >
            {product}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

// ── ImageCard ────────────────────────────────────────────────────────────────
interface CardInfo {
  description: string;
  products: string[];
  waMessage: string;
  variant?: 'cream' | 'olive' | 'dark';
}
interface ImageCardProps {
  bgImage: string;
  eyebrow?: string;
  title?: string;
  wide?: boolean;
  showPlay?: boolean;
  carouselImages?: string[];
  info?: CardInfo;
  delay?: number;
}

function ImageCard({ bgImage, eyebrow, title, wide = false, showPlay = false, carouselImages, info, delay = 0 }: ImageCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [ripple, setRipple] = useState<{ id: number; x: number; y: number; r: number } | null>(null);
  const rippleIdRef = useRef(0);

  const toggleExpand = (e: React.MouseEvent) => {
    if (!info || !ref.current) return;
    e.stopPropagation();
    const r = ref.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const maxR = Math.hypot(Math.max(x, r.width - x), Math.max(y, r.height - y)) + 20;
    setRipple({ id: rippleIdRef.current++, x, y, r: maxR });
    setExpanded(prev => !prev);
  };
  const waLink = info
    ? `https://wa.me/573145551717?text=${encodeURIComponent(info.waMessage)}`
    : 'https://wa.me/573145551717';

  // Parallax: background drifts within ±12% of the card height as it scrolls past
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-12%', '12%']);

  // Magnetic corner arrow: tracks cursor with spring-smoothed offset (±18 px max)
  const arrowMX = useMotionValue(0);
  const arrowMY = useMotionValue(0);
  const arrowSX = useSpring(arrowMX, { stiffness: 180, damping: 18, mass: 0.6 });
  const arrowSY = useSpring(arrowMY, { stiffness: 180, damping: 18, mass: 0.6 });

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!arrowRef.current) return;
    const ar = arrowRef.current.getBoundingClientRect();
    const cx = ar.left + ar.width / 2;
    const cy = ar.top  + ar.height / 2;
    const dx = (e.clientX - cx) * 0.13;
    const dy = (e.clientY - cy) * 0.13;
    arrowMX.set(Math.max(-18, Math.min(18, dx)));
    arrowMY.set(Math.max(-18, Math.min(18, dy)));
  };

  const handleLeave = () => {
    setHovered(false);
    arrowMX.set(0);
    arrowMY.set(0);
  };

  // Split title into words while preserving '<br>' line breaks — for stagger reveal
  const titleLines = title ? title.split('<br>').map(l => l.split(' ').filter(Boolean)) : [];
  let wordCounter = 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 44 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.75, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={`card-wrap relative overflow-hidden ${wide ? 'md:col-span-2' : ''}`}
      style={{ height: 'clamp(340px, 48vw, 686px)', borderRadius: 14, background: '#000', cursor: info ? 'pointer' : 'default' }}
      onMouseEnter={() => setHovered(true)}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={info ? toggleExpand : undefined}
    >
      {carouselImages ? (
        <Slideshow images={carouselImages} />
      ) : (
        <motion.div
          className="absolute card-bg"
          style={{
            top: '-15%', left: 0, right: 0, bottom: '-15%',
            y: bgY,
            backgroundImage: `url('${bgImage}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            willChange: 'transform',
          }}
          animate={{ scale: hovered ? 1.06 : 1 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        />
      )}

      <div className="absolute inset-0" style={{ background: 'linear-gradient(rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 29%)', zIndex: 2 }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 100%)', zIndex: 2 }} />

      {/* Tech-scan: thin top edge line that sweeps across on hover */}
      <motion.div
        className="absolute z-[3]"
        style={{ top: 0, left: 0, right: 0, height: 1, background: C.oliveLight, transformOrigin: '0% 50%' }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: hovered && !expanded ? 1 : 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      />

      {/* Ripple bg — grows from click point. Color depends on the card's variant. */}
      <AnimatePresence>
        {expanded && ripple && (
          <motion.div
            key={'exp-' + ripple.id}
            aria-hidden
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: 'absolute',
              left: ripple.x - ripple.r,
              top:  ripple.y - ripple.r,
              width: ripple.r * 2, height: ripple.r * 2,
              borderRadius: '50%',
              background: info?.variant === 'cream' ? 'rgb(252,246,232)'
                       : info?.variant === 'olive' ? 'rgb(153,159,86)'
                       : 'rgb(91,92,48)',
              zIndex: 6,
              pointerEvents: 'none',
            }}
          />
        )}
      </AnimatePresence>

      {/* Corner arrow — visible on hover; hidden when expanded */}
      {title && info && !expanded && (
        <motion.div
          ref={arrowRef}
          className="absolute z-10 flex items-center justify-center"
          style={{
            top: 16, right: 16, width: 44, height: 44, borderRadius: '50%',
            background: C.oliveLight, color: '#fff',
            x: arrowSX, y: arrowSY,
          }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: hovered ? 1 : 0, scale: hovered ? 1 : 0.6 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
          <ArrowRight size={20} strokeWidth={2.5} />
        </motion.div>
      )}

      {/* Info panel — fades in after the ripple covers the card; colors flip per variant */}
      <AnimatePresence>
        {expanded && info && (() => {
          const variant = info.variant ?? 'dark';
          // bg = the ripple/card color; fg = the text/foreground color
          const bg = variant === 'cream' ? 'rgb(252,246,232)'
                   : variant === 'olive' ? 'rgb(153,159,86)'
                   : 'rgb(91,92,48)';
          const fg = variant === 'cream' ? 'rgb(91,92,48)' : 'rgb(252,246,232)';
          const fgRGB = variant === 'cream' ? '91,92,48' : '252,246,232';
          const fgAlpha = (a: number) => `rgba(${fgRGB},${a})`;
          return (
            <motion.div
              key="info-panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, delay: 0.28 }}
              className="absolute inset-0 z-20 flex flex-col"
              style={{ padding: 'clamp(20px, 3vw, 36px)', color: fg }}
            >
              <button
                aria-label="Cerrar"
                onClick={toggleExpand}
                style={{
                  position: 'absolute', top: 16, right: 16,
                  width: 44, height: 44, borderRadius: '50%',
                  background: fgAlpha(0.12),
                  border: `1px solid ${fgAlpha(0.32)}`,
                  color: fg,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = fgAlpha(0.22)}
                onMouseLeave={e => e.currentTarget.style.background = fgAlpha(0.12)}
              >
                <X size={18} strokeWidth={2.5} />
              </button>

              {eyebrow && (
                <div style={{ fontSize: 'clamp(14px, 1.7vw, 18px)', fontWeight: 500, letterSpacing: '2px', textTransform: 'uppercase', opacity: 0.85, marginBottom: 12 }}>
                  {eyebrow}
                </div>
              )}
              {title && (
                <div
                  style={{ fontSize: 'clamp(22px, 3vw, 40px)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-1px', marginBottom: 'clamp(14px, 2vw, 22px)' }}
                >
                  {title.split('<br>').map((part, i, arr) => (
                    <React.Fragment key={i}>
                      {part}
                      {i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              )}

              <p style={{ fontSize: 'clamp(15px, 1.4vw, 18px)', lineHeight: 1.5, opacity: 0.9, maxWidth: 560, marginBottom: 'clamp(18px, 2.4vw, 28px)' }}>
                {info.description}
              </p>

              <div className="flex flex-wrap gap-2" style={{ marginBottom: 'auto' }}>
                {info.products.map(p => (
                  <span key={p} style={{
                    display: 'inline-flex', alignItems: 'center',
                    height: 34, padding: '0 16px',
                    borderRadius: 99,
                    border: `1px solid ${fgAlpha(0.4)}`,
                    background: fgAlpha(0.07),
                    fontSize: 14, fontWeight: 500,
                    color: fg,
                  }}>
                    {p}
                  </span>
                ))}
              </div>

              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                className="inline-flex items-center gap-2 self-start no-underline"
                style={{
                  marginTop: 'clamp(18px, 2vw, 24px)',
                  background: fg,
                  color: bg,
                  fontWeight: 700,
                  fontSize: 'clamp(15px, 1.4vw, 17px)',
                  padding: '12px 20px',
                  borderRadius: 99,
                  transition: 'transform 0.2s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}
              >
                Hablar por WhatsApp
                <ArrowRight size={16} strokeWidth={2.5} />
              </a>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {(eyebrow || title) && !expanded && (
        <motion.div
          className="absolute z-10"
          style={{ left: 16, bottom: 16, right: 16, color: '#fff', maxWidth: wide ? 1300 : 630 }}
          animate={{ y: hovered ? -8 : 0 }}
          transition={{ duration: 0.32, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {eyebrow && (
            <div style={{ overflow: 'hidden', fontSize: 'clamp(16px, 2.5vw, 27px)', fontWeight: 400, lineHeight: 1.1, marginBottom: 6 }}>
              <motion.div
                initial={{ y: '110%' }}
                animate={inView ? { y: '0%' } : {}}
                transition={{ duration: 0.7, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                style={{ opacity: hovered ? 1 : 0.78, transition: 'opacity 0.3s' }}
              >
                {eyebrow}
              </motion.div>
            </div>
          )}
          {title && (
            <div style={{ display: 'flex', alignItems: 'center', fontSize: 'clamp(22px, 3.5vw, 48px)', fontWeight: 700, lineHeight: 1.05, letterSpacing: '-1.656px' }}>
              {/* Words mask-reveal with stagger */}
              <div style={{ display: 'inline-block' }}>
                {titleLines.map((words, lineIdx) => (
                  <div key={lineIdx} style={{ display: 'block' }}>
                    {words.map((word) => {
                      const idx = wordCounter++;
                      return (
                        <span key={lineIdx + '-' + idx} style={{ display: 'inline-block', overflow: 'hidden', verticalAlign: 'bottom', marginRight: '0.27em' }}>
                          <motion.span
                            initial={{ y: '115%' }}
                            animate={inView ? { y: '0%' } : {}}
                            transition={{ duration: 0.75, delay: 0.28 + idx * 0.055, ease: [0.16, 1, 0.3, 1] }}
                            style={{ display: 'inline-block' }}
                          >
                            {word}
                          </motion.span>
                        </span>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {showPlay && !carouselImages && (
        <motion.div
          className="absolute z-10 flex items-center justify-center"
          style={{ right: 24, bottom: 24, width: 60, height: 60, borderRadius: '50%', background: C.dark }}
          animate={{ scale: hovered ? 1.12 : 1 }}
          transition={{ duration: 0.28 }}
        >
          <div className="flex gap-1">
            <span style={{ display: 'block', width: 4, height: 14, background: '#fff' }} />
            <span style={{ display: 'block', width: 4, height: 14, background: '#fff' }} />
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Flexivo() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const WHATSAPP_URL = 'https://wa.me/573145551717';
  const navLinks: { label: string; target?: string; href?: string }[] = [
    { label: 'Productos', target: 'productos' },
    { label: 'Sectores',  target: 'sectores' },
    { label: 'Contacto',  href: WHATSAPP_URL },
  ];

  const sectors = [
    'Alimentos y bebidas', 'Retail', 'Farmacéutico', 'Industrial',
    'Logística', 'Consumo masivo', 'Comercio en general',
  ];
  const products = [
    'Termoencogible', 'Películas plásticas', 'Empaques flexibles', 'Stretch film',
    'Bolsas industriales', 'Materiales de protección', 'Productos personalizados',
    'Polipropileno moniorientado', 'Polipropileno biorentado',
  ];
  const footerImages = [ASSETS.hero2, ASSETS.food1, ASSETS.robot, ASSETS.materials1, ASSETS.pharma1, ASSETS.industrial1, ASSETS.retail2];

  // Per-sector info shown when a card is expanded.
  // Variants alternate cream ↔ dark olive across the grid so no two adjacent
  // cards share the same expanded color.
  const CARD_INFO: Record<string, CardInfo> = {
    food: {
      description: 'Empaques que preservan frescura, optimizan exhibición y reducen pérdidas en líneas de alimentos y bebidas.',
      products: ['Termoencogible', 'Películas plásticas', 'Empaques flexibles'],
      waMessage: 'Hola FLEXIVO, me interesa el sector de Alimentos y bebidas.',
      variant: 'dark',
    },
    pharma: {
      description: 'Soluciones de empaque con altos estándares de control, precisión y trazabilidad para el sector farmacéutico.',
      products: ['Empaques flexibles', 'Materiales de protección', 'Productos personalizados'],
      waMessage: 'Hola FLEXIVO, me interesa el sector Farmacéutico.',
      variant: 'cream',
    },
    industrial: {
      description: 'Empaques resistentes para almacenamiento, transporte y logística en operaciones industriales.',
      products: ['Stretch film', 'Bolsas industriales', 'Materiales de protección'],
      waMessage: 'Hola FLEXIVO, me interesa el sector Industrial.',
      variant: 'olive',
    },
    materials: {
      description: 'Películas de polipropileno monoorientado (MOPP) y biorientado (BOPP) de alto desempeño mecánico y óptico.',
      products: ['Polipropileno MOPP', 'Polipropileno BOPP', 'Materiales de protección'],
      waMessage: 'Hola FLEXIVO, me interesan los materiales MOPP y BOPP.',
      variant: 'dark',
    },
    retail1: {
      description: 'Bolsas industriales y empaques flexibles diseñados para optimizar exhibición y rotación en retail.',
      products: ['Bolsas industriales', 'Empaques flexibles', 'Productos personalizados'],
      waMessage: 'Hola FLEXIVO, me interesa el sector Retail y consumo masivo.',
      variant: 'olive',
    },
    retail2: {
      description: 'Soluciones de empaque para consumo masivo con foco en eficiencia operativa y presentación de marca.',
      products: ['Empaques flexibles', 'Termoencogible', 'Productos personalizados'],
      waMessage: 'Hola FLEXIVO, me interesa empaque para consumo masivo.',
      variant: 'cream',
    },
  };

  return (
    <div className="min-h-screen" style={{ background: C.cream, fontFamily: "'Noto Sans', sans-serif", color: C.dark }}>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <motion.header
        className="flex items-center justify-between w-full sticky top-0 z-50"
        style={{ height: 96, padding: '0 clamp(24px, 12.5vw, 240px)', background: C.cream }}
        animate={{
          boxShadow: scrolled ? '0 1px 24px rgba(0,0,0,0.07)' : '0 0 0 rgba(0,0,0,0)',
          height: scrolled ? 72 : 96,
        }}
        transition={{ duration: 0.3 }}
      >
        <motion.img
          src={ASSETS.logo} alt="FLEXIVO"
          style={{ height: 38, width: 'auto', cursor: 'pointer' }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          whileHover={{ opacity: 0.8 }}
          transition={{ duration: 0.2 }}
        />
        <nav className="hidden md:flex" style={{ gap: 48 }}>
          {navLinks.map(({ label, target, href }) => (
            <a
              key={label}
              className="nav-link"
              {...(href
                ? { href, target: '_blank', rel: 'noopener noreferrer' }
                : { onClick: () => target && scrollTo(target) })}
            >
              {label}
            </a>
          ))}
        </nav>
        <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22 }}
            className="md:hidden px-6 py-4 sticky top-[72px] z-40"
            style={{ background: C.cream, borderBottom: '1px solid rgba(0,0,0,0.06)' }}
          >
            {navLinks.map(({ label, target, href }) => (
              <a
                key={label}
                className="block py-3 no-underline"
                style={{ color: C.dark, fontSize: 18 }}
                {...(href
                  ? { href, target: '_blank', rel: 'noopener noreferrer', onClick: () => setMobileMenuOpen(false) }
                  : { onClick: () => { target && scrollTo(target); setMobileMenuOpen(false); } })}
              >
                {label}
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Main ─────────────────────────────────────────────────────────── */}
      <main className="mx-auto" style={{ maxWidth: 1440, padding: '0 24px' }}>

        {/* Hero grid */}
        <section
          className="flexivo-hero-grid grid gap-[17px] mb-5"
          style={{ gridTemplateColumns: 'minmax(0, 921fr) minmax(0, 519fr)', height: 'clamp(400px, 60vw, 918px)' }}
        >
          {/* Hero left */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative overflow-hidden"
            style={{ borderRadius: 14, background: '#000' }}
          >
            <Slideshow images={[
              ASSETS.heroPrint,
              ASSETS.hero1,
              ASSETS.hero3,
              ASSETS.hero4,
            ]} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 22%)', zIndex: 2 }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 100%)', zIndex: 2 }} />
            <motion.div
              className="absolute z-10"
              style={{ left: 16, bottom: 24, right: 16, color: '#fff', maxWidth: 900 }}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <div style={{ fontSize: 'clamp(14px, 2.5vw, 27px)', fontWeight: 400, lineHeight: 1, marginBottom: 8, opacity: 0.85 }}>
                FLEXIVO · Soluciones Integrales de Empaques
              </div>
              <div style={{ fontSize: 'clamp(28px, 3.3vw, 48px)', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-1.656px' }}>
                Calidad, eficiencia y acompañamiento<br />en cada proyecto
              </div>
            </motion.div>
          </motion.div>

          {/* Hero right — CTA: liquid goo on hover */}
          <CTACard />
        </section>

        {/* Row: Robot carousel + Food */}
        <section className="flexivo-2col grid grid-cols-2 gap-5 mb-5">
          <ImageCard bgImage={ASSETS.robotTop} carouselImages={ROBOT_SLIDES} delay={0} />
          <ImageCard bgImage={ASSETS.food1} eyebrow="Alimentos y bebidas" title="Termoencogible y películas<br>plásticas" info={CARD_INFO.food} delay={0.1} />
        </section>

        {/* Sectors + Products */}
        <section
          id="sectores"
          className="flexivo-sectors-grid grid mb-5 overflow-hidden"
          style={{ gridTemplateColumns: '395px 1px 1fr', height: 'clamp(500px, 51vw, 740px)', borderRadius: 14, background: C.oliveLight }}
        >
          {/* Left */}
          <FadeUp direction="left" className="flexivo-sectors-left relative h-full" style={{ padding: '65px 63px' }}>
            <div className="absolute inset-0" style={{ background: `linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.72)), url('${ASSETS.sectorsBg}') center/cover no-repeat` }} />
            <div className="relative z-10">
              <div style={{ fontWeight: 500, fontSize: 20, letterSpacing: '2.863px', lineHeight: '30.673px', color: 'rgba(255,251,240,0.5)' }}>
                INDUSTRIAS
              </div>
              <div style={{ fontSize: 'clamp(28px, 3.5vw, 47.7px)', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.852px', color: '#fff', whiteSpace: 'pre-line', marginTop: 16 }}>
                {`Sectores que\natendemos`}
              </div>
              <div className="flex flex-col" style={{ marginTop: 32 }}>
                {sectors.map((sector, i) => (
                  <motion.div
                    key={sector}
                    className="sector-link"
                    style={{ fontSize: 'clamp(16px, 2vw, 24px)', fontWeight: 400 }}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 + i * 0.055 }}
                  >
                    {sector}
                  </motion.div>
                ))}
              </div>
            </div>
          </FadeUp>

          <div className="flexivo-sectors-divider" style={{ background: 'rgba(255,255,255,0.1)' }} />

          {/* Right — liquid goo on hover */}
          <CatalogCard products={products} />
        </section>

        {/* Row: Pharma + Industrial */}
        <section className="flexivo-2col grid grid-cols-2 gap-5 mb-5">
          <ImageCard bgImage={ASSETS.pharma1} eyebrow="Sector farmacéutico" title="Precisión y protección<br>en cada entrega" info={CARD_INFO.pharma} delay={0} />
          <ImageCard bgImage={ASSETS.industrial1} eyebrow="Sector industrial" title="Empaques para<br>almacenamiento y logística" info={CARD_INFO.industrial} delay={0.1} />
        </section>

        {/* Full width: Materials */}
        <section className="flexivo-2col grid grid-cols-2 gap-5 mb-5">
          <ImageCard bgImage={ASSETS.materials1} eyebrow="Polipropileno MOPP y BOPP" title="Materiales de protección de alto desempeño" info={CARD_INFO.materials} wide delay={0} />
        </section>

        {/* Row: Retail carousel + Retail 2 */}
        <section className="flexivo-2col grid grid-cols-2 gap-5 mb-5">
          <ImageCard bgImage={ASSETS.retail1} eyebrow="Retail y consumo masivo" title="Bolsas industriales y<br>empaques flexibles" carouselImages={RETAIL1_SLIDES} info={CARD_INFO.retail1} delay={0} />
          <ImageCard bgImage={ASSETS.retail2} eyebrow="Sector retail y consumo" title="Empaques para<br>consumo masivo" info={CARD_INFO.retail2} delay={0.1} />
        </section>
      </main>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer id="contacto" className="relative" style={{ background: '#000', color: '#fff', padding: '140px 24px 60px', marginTop: 40 }}>
        {/* Image strip */}
        <FadeUp delay={0}>
          <div className="mx-auto relative overflow-hidden" style={{ maxWidth: 1232, marginBottom: 110, height: 'clamp(250px, 34vw, 497px)', borderRadius: 12, overflow: 'hidden' }}>
            <div className="flexivo-footer-strip grid h-full" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 7 }}>
              {footerImages.map((img, i) => (
                <div
                  key={i}
                  className="footer-img-item"
                  style={{ backgroundImage: `url('${img}')`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#222' }}
                />
              ))}
            </div>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'rgba(17,17,17,0.25)' }} />
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="flexivo-footer-links flex flex-wrap justify-center items-center gap-6 mb-4" style={{ fontSize: 24 }}>
            {([
              { label: 'Sectores',  target: 'sectores' },
              { label: 'Productos', target: 'productos' },
              { label: 'Contacto',  href: WHATSAPP_URL },
            ] as { label: string; target?: string; href?: string }[]).map((link, i, arr) => (
              <React.Fragment key={link.label}>
                <a
                  className="no-underline"
                  style={{ color: '#fff', transition: 'opacity 0.2s', textDecoration: 'none', cursor: 'pointer' }}
                  {...(link.href
                    ? { href: link.href, target: '_blank', rel: 'noopener noreferrer' }
                    : { onClick: () => link.target && scrollTo(link.target) })}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '0.65')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                >
                  {link.label}
                </a>
                {i < arr.length - 1 && <span style={{ opacity: 0.4, fontSize: 18 }}>•</span>}
              </React.Fragment>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.18}>
          <div className="flex flex-wrap justify-center items-center gap-5 mb-3.5 px-4" style={{ fontSize: 'clamp(13px, 1.5vw, 18px)', color: '#fff', opacity: 0.55 }}>
            <a href="https://wa.me/573145551717" target="_blank" rel="noopener noreferrer"
               style={{ color: '#fff', textDecoration: 'none' }}>
              Tel: +57 314 555 17 17
            </a>
            <span>•</span>
            <a href="mailto:flexivosas@gmail.com"
               style={{ color: '#fff', textDecoration: 'none' }}>
              flexivosas@gmail.com
            </a>
          </div>
          <div className="text-center" style={{ fontSize: 18, color: '#fff', opacity: 0.45 }}>
            © FLEXIVO S.A.S. 2024 - 2025
          </div>
        </FadeUp>
      </footer>
    </div>
  );
}
